import { api, type GoogleIntegrationStatusData } from "./api";
import { GOOGLE_SCOPE_BUNDLES } from "./googleScopes";

export type GoogleConsentScopeStatus = {
  granted: boolean;
  connectionStatus: string;
  missingScopes: string[];
};

export type GoogleConsentStatus = {
  receipt: GoogleConsentScopeStatus;
  bundle: GoogleConsentScopeStatus;
  calendarToday: GoogleConsentScopeStatus;
};

type GoogleConsentStatusDebugScope = {
  ok: boolean;
  granted?: boolean;
  connectionStatus?: string;
  missingScopes?: string[];
  error?: {
    code?: string;
    message?: string;
  };
};

type GoogleConsentStatusDebug = {
  timestamp: string;
  receipt: GoogleConsentStatusDebugScope;
  bundle: GoogleConsentStatusDebugScope;
  calendarToday: GoogleConsentStatusDebugScope;
};

function toGoogleConsentScopeStatus(status: GoogleIntegrationStatusData): GoogleConsentScopeStatus {
  return {
    granted: status.scopeBundleGranted === true,
    connectionStatus: status.connectionStatus ?? "",
    missingScopes: Array.isArray(status.missingScopes) ? status.missingScopes : [],
  };
}

function createDefaultGoogleConsentScopeStatus(): GoogleConsentScopeStatus {
  return {
    granted: false,
    connectionStatus: "",
    missingScopes: [],
  };
}

function toErrorDebug(error: unknown): GoogleConsentStatusDebugScope["error"] {
  if (!error || typeof error !== "object") {
    return {
      message: typeof error === "string" ? error : undefined,
    };
  }

  const candidate = error as { code?: unknown; message?: unknown };
  return {
    code: typeof candidate.code === "string" ? candidate.code : undefined,
    message: typeof candidate.message === "string" ? candidate.message : undefined,
  };
}

function logGoogleConsentStatusDebug(debug: GoogleConsentStatusDebug) {
  if (typeof window !== "undefined") {
    (window as any).__wizmateGoogleConsentStatusDebug = debug;
  }
}

export async function getGoogleConsentStatus(): Promise<GoogleConsentStatus> {
  const [receiptStatusResult, bundleStatusResult, calendarTodayStatusResult] = await Promise.allSettled([
    api.getGoogleIntegrationStatus(GOOGLE_SCOPE_BUNDLES.receipt),
    api.getGoogleIntegrationStatus(GOOGLE_SCOPE_BUNDLES.bundle),
    api.getGoogleIntegrationStatus(GOOGLE_SCOPE_BUNDLES.calendarToday),
  ]);

  const receiptStatus =
    receiptStatusResult.status === "fulfilled"
      ? toGoogleConsentScopeStatus(receiptStatusResult.value)
      : createDefaultGoogleConsentScopeStatus();
  const bundleStatus =
    bundleStatusResult.status === "fulfilled"
      ? toGoogleConsentScopeStatus(bundleStatusResult.value)
      : createDefaultGoogleConsentScopeStatus();
  const calendarTodayStatus =
    calendarTodayStatusResult.status === "fulfilled"
      ? toGoogleConsentScopeStatus(calendarTodayStatusResult.value)
      : createDefaultGoogleConsentScopeStatus();

  logGoogleConsentStatusDebug({
    timestamp: new Date().toISOString(),
    receipt:
      receiptStatusResult.status === "fulfilled"
        ? {
            ok: true,
            granted: receiptStatus.granted,
            connectionStatus: receiptStatus.connectionStatus,
            missingScopes: receiptStatus.missingScopes,
          }
        : {
            ok: false,
            error: toErrorDebug(receiptStatusResult.reason),
          },
    bundle:
      bundleStatusResult.status === "fulfilled"
        ? {
            ok: true,
            granted: bundleStatus.granted,
            connectionStatus: bundleStatus.connectionStatus,
            missingScopes: bundleStatus.missingScopes,
          }
        : {
            ok: false,
            error: toErrorDebug(bundleStatusResult.reason),
          },
    calendarToday:
      calendarTodayStatusResult.status === "fulfilled"
        ? {
            ok: true,
            granted: calendarTodayStatus.granted,
            connectionStatus: calendarTodayStatus.connectionStatus,
            missingScopes: calendarTodayStatus.missingScopes,
          }
        : {
            ok: false,
            error: toErrorDebug(calendarTodayStatusResult.reason),
          },
  });

  return {
    receipt: receiptStatus,
    bundle: bundleStatus,
    calendarToday: calendarTodayStatus,
  };
}
